import { lazy, Suspense } from "react";
import Navbar from "@/components/landing/Navbar";
import DeferredRender from "@/components/DeferredRender";
import ScrollSection from "@/components/analytics/ScrollSection";
import RouteSeo from "@/components/seo/RouteSeo";
import { SITE_URL } from "@/lib/siteConfig";
import { scrollToSection } from "@/lib/scrollToSection";

/**
 * The product deep-dive.
 *
 * Sections that used to stretch the landing page live here, so the landing
 * page can stay a single clear promise and this page can carry the detail for
 * people who want it.
 */

const Features = lazy(() => import("@/components/landing/Features"));
const TwoWaysToUse = lazy(() => import("@/components/landing/TwoWaysToUse"));
const Principles = lazy(() => import("@/components/landing/Principles"));
const Manifesto = lazy(() => import("@/components/landing/Manifesto"));
const HowPredictionsWork = lazy(() => import("@/components/landing/HowPredictionsWork"));
const ShortFAQ = lazy(() => import("@/components/landing/ShortFAQ"));
const SocialProof = lazy(() => import("@/components/landing/SocialProof"));
const BlogPreview = lazy(() => import("@/components/landing/BlogPreview"));
const FinalCTA = lazy(() => import("@/components/landing/FinalCTA"));
const Footer = lazy(() => import("@/components/landing/Footer"));

const PAGE_TITLE = "How Myelina forecasts your energy · Product deep-dive";
const PAGE_DESCRIPTION =
  "See what Myelina Health does with a 45-second check-in: the daily energy forecast, how the pattern is read, the privacy principles behind it, and answers to the questions people ask most.";

/** Deep-dive sections, in page order. Drives the jump nav and the ItemList schema. */
const SECTIONS = [
  { id: "features", label: "What you get", description: "The daily forecast, the energy map, and the check-in that feeds them." },
  { id: "two-ways-to-use", label: "Two ways to use it", description: "Plan the day ahead, or look back and learn the pattern." },
  { id: "how-predictions-work", label: "How predictions work", description: "What the model reads, and how confident it says it is." },
  { id: "principles", label: "Principles", description: "What the forecast will never do, and why." },
  { id: "manifesto", label: "Why we built it", description: "Energy is a budget. The forecast helps you spend it on purpose." },
  { id: "short-faq", label: "Questions", description: "Privacy, accuracy, cost, and who it is for." },
] as const;

const CANONICAL = `${SITE_URL}/product`;

const JSON_LD = [
  {
    id: "product-jsonld-webpage",
    data: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${CANONICAL}#webpage`,
      url: CANONICAL,
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", url: `${SITE_URL}/`, name: "Myelina Health" },
      about: {
        "@type": "SoftwareApplication",
        name: "Myelina Health",
        applicationCategory: "HealthApplication",
        operatingSystem: "iOS, Web",
      },
    },
  },
  {
    id: "product-jsonld-breadcrumbs",
    data: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Product", item: CANONICAL },
      ],
    },
  },
  {
    id: "product-jsonld-sections",
    data: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Myelina product deep-dive sections",
      itemListElement: SECTIONS.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: s.label,
        description: s.description,
        url: `${CANONICAL}#${s.id}`,
      })),
    },
  },
];

const Product = () => {
  return (
    <div className="min-h-screen bg-background">
      <RouteSeo
        path="/product"
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        imageAlt="Myelina Health product overview: daily energy forecast and energy map"
        jsonLd={JSON_LD}
      />
      <Navbar />
      <main>
        <section className="container mx-auto px-6 sm:px-10 lg:px-16 pt-32 pb-10 text-center">
          <div className="eyebrow mb-3">Inside Myelina</div>
          <h1 className="font-display text-4xl sm:text-5xl text-foreground [text-wrap:balance] max-w-3xl mx-auto">
            How the forecast is built, and what it will never do
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            The whole product in one place: what you get, how the pattern is read, the
            principles behind it, and the answers people ask for most.
          </p>

          <nav aria-label="Jump to a section" className="mt-8 flex flex-wrap justify-center gap-2">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(s.id);
                }}
                className="inline-flex min-h-[44px] items-center rounded-full border border-border bg-card px-4 text-sm text-muted-foreground transition-colors hover:text-foreground hover:border-foreground/30"
              >
                {s.label}
              </a>
            ))}
          </nav>
        </section>


        <DeferredRender id="features" minHeight="2400px">
          <Suspense fallback={<div className="min-h-[40vh]" aria-hidden="true" />}>
            <ScrollSection page="product" name="features" as="div"><Features /></ScrollSection>
            <ScrollSection page="product" name="two_ways_to_use" as="div"><div id="two-ways-to-use"><TwoWaysToUse /></div></ScrollSection>
            <ScrollSection page="product" name="how_predictions_work" as="div"><HowPredictionsWork /></ScrollSection>
          </Suspense>
        </DeferredRender>

        <DeferredRender minHeight="2400px">
          <Suspense fallback={<div className="min-h-[40vh]" aria-hidden="true" />}>
            <ScrollSection page="product" name="principles" as="div"><Principles /></ScrollSection>
            <ScrollSection page="product" name="manifesto" as="div"><div id="manifesto"><Manifesto /></div></ScrollSection>
            <ScrollSection page="product" name="social_proof" as="div"><SocialProof /></ScrollSection>
            <ScrollSection page="product" name="short_faq" as="div"><ShortFAQ /></ScrollSection>
            <ScrollSection page="product" name="blog_preview" as="div"><BlogPreview /></ScrollSection>
          </Suspense>
        </DeferredRender>

        <DeferredRender minHeight="640px">
          <Suspense fallback={<div className="min-h-[40vh]" aria-hidden="true" />}>
            <ScrollSection page="product" name="final_cta" as="div"><FinalCTA /></ScrollSection>
          </Suspense>
        </DeferredRender>
      </main>
      <DeferredRender minHeight="480px">
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </DeferredRender>
    </div>
  );
};

export default Product;
